import { useState, useEffect } from 'react'
import { MaterialFormData, UnitsResponse } from './types'
import { apiClient } from '@/services/api'

export const useMaterialForm = (initialData?: Partial<MaterialFormData>) => {
  const [formData, setFormData] = useState<MaterialFormData>({
    name: '',
    category: '',
    baseUnit: '',
    sku: '',
    description: '',
    brand: '',
    reorderPoint: 0,
    maxStock: 0,
    specifications: {},
    includePurchase: false,
    purchaseQuantity: 0,
    purchaseUnit: '',
    convertedQuantity: 0,
    costPerUnit: 0,
    totalCost: 0,
    costInputType: 'per-unit',
    isPackagePurchase: false,
    packageQuantity: 0,
    itemsPerPackage: 0,
    packageCost: 0,
    supplier: '',
    supplierId: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    invoiceNumber: '',
    notes: '',
    ...initialData
  })

  const [availableUnits, setAvailableUnits] = useState<UnitsResponse | null>(null)
  const [conversionLoading, setConversionLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fetch available units on mount
  useEffect(() => {
    fetchAvailableUnits()
  }, [])

  // Auto-set purchase unit when base unit changes
  useEffect(() => {
    if (formData.baseUnit && !formData.purchaseUnit) {
      setFormData(prev => ({ ...prev, purchaseUnit: prev.baseUnit }))
    }
  }, [formData.baseUnit])

  // Auto-detect package purchase based on base unit
  useEffect(() => {
    // Units that require package/batch purchasing logic
    const packageUnits = ['package', 'set', 'dozen', 'gross', 'strand', 'pair']
    const isPackageUnit = packageUnits.includes(formData.baseUnit)
    
    // Auto-set items per package for fixed-count units
    let itemsPerPackage = formData.itemsPerPackage
    if (isPackageUnit) {
      const fixedCounts: Record<string, number> = {
        'dozen': 12,
        'gross': 144,
        'pair': 2
      }
      if (fixedCounts[formData.baseUnit]) {
        itemsPerPackage = fixedCounts[formData.baseUnit]
      }
    }
    
    setFormData(prev => ({
      ...prev,
      isPackagePurchase: isPackageUnit,
      itemsPerPackage: itemsPerPackage
    }))
  }, [formData.baseUnit])

  // Auto-calculate package quantities and costs
  useEffect(() => {
    if (formData.isPackagePurchase && formData.packageQuantity > 0 && formData.itemsPerPackage > 0) {
      const totalItems = formData.packageQuantity * formData.itemsPerPackage
      const totalCost = formData.packageQuantity * formData.packageCost
      const costPerItem = formData.packageCost > 0 ? formData.packageCost / formData.itemsPerPackage : 0
      
      setFormData(prev => ({
        ...prev,
        purchaseQuantity: totalItems,
        convertedQuantity: totalItems,
        totalCost: totalCost,
        costPerUnit: costPerItem
      }))
    }
  }, [formData.packageQuantity, formData.itemsPerPackage, formData.packageCost, formData.isPackagePurchase])

  // Auto-convert quantities when purchase quantity or units change (for non-package purchases)
  useEffect(() => {
    if (!formData.isPackagePurchase && formData.purchaseQuantity > 0 && formData.purchaseUnit && formData.baseUnit && formData.purchaseUnit !== formData.baseUnit) {
      convertPurchaseQuantity()
    } else if (!formData.isPackagePurchase && formData.purchaseUnit === formData.baseUnit) {
      setFormData(prev => ({ ...prev, convertedQuantity: prev.purchaseQuantity }))
    }
  }, [formData.purchaseQuantity, formData.purchaseUnit, formData.baseUnit, formData.isPackagePurchase])

  // Auto-calculate costs when quantities or cost input changes (for non-package purchases)
  useEffect(() => {
    if (formData.includePurchase && !formData.isPackagePurchase) {
      calculateCosts()
    }
  }, [formData.costInputType, formData.totalCost, formData.costPerUnit, formData.convertedQuantity, formData.isPackagePurchase])

  const fetchAvailableUnits = async () => {
    try {
      const response = await apiClient.get('/materials/units')
      if (response) {
        setAvailableUnits(response)
      }
    } catch (error) {
      console.error('Error fetching available units:', error)
      setAvailableUnits(null)
    }
  }

  const convertPurchaseQuantity = async () => {
    if (!formData.purchaseQuantity || !formData.purchaseUnit || !formData.baseUnit) return

    setConversionLoading(true)
    try {
      const response = await apiClient.post('/materials/convert-units', {
        value: formData.purchaseQuantity,
        fromUnit: formData.purchaseUnit,
        toUnit: formData.baseUnit
      })
      
      setFormData(prev => ({ 
        ...prev, 
        convertedQuantity: response.convertedValue 
      }))
    } catch (error) {
      console.error('Error converting units:', error)
      setFormData(prev => ({ ...prev, convertedQuantity: 0 }))
    } finally {
      setConversionLoading(false)
    }
  }

  const calculateCosts = () => {
    setFormData(prev => {
      const newData = { ...prev }
      
      if (newData.costInputType === 'per-unit') {
        // User entered cost per unit, calculate total cost
        if (newData.convertedQuantity > 0) {
          newData.totalCost = newData.costPerUnit * newData.convertedQuantity
        }
      } else {
        // User entered total cost, calculate cost per unit
        if (newData.convertedQuantity > 0) {
          newData.costPerUnit = newData.totalCost / newData.convertedQuantity
        }
      }
      
      return newData
    })
  }

  const handleInputChange = (field: keyof MaterialFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Required material fields
    if (!formData.name.trim()) newErrors.name = 'Material name is required'
    if (!formData.category) newErrors.category = 'Category is required'
    if (!formData.baseUnit) newErrors.baseUnit = 'Base unit is required'

    // Purchase validation if included
    if (formData.includePurchase) {
      if (formData.isPackagePurchase) {
        // Package purchase validation
        if (formData.packageQuantity <= 0) newErrors.packageQuantity = 'Number of packages must be greater than 0'
        if (formData.itemsPerPackage <= 0) newErrors.itemsPerPackage = 'Items per package must be greater than 0'
        if (formData.packageCost <= 0) newErrors.packageCost = 'Package cost must be greater than 0'
      } else {
        // Regular purchase validation
        if (formData.purchaseQuantity <= 0) newErrors.purchaseQuantity = 'Purchase quantity must be greater than 0'
        if (!formData.purchaseUnit) newErrors.purchaseUnit = 'Purchase unit is required'
        
        // Validate cost based on input type
        if (formData.costInputType === 'per-unit') {
          if (formData.costPerUnit <= 0) newErrors.costPerUnit = 'Cost per unit must be greater than 0'
        } else {
          if (formData.totalCost <= 0) newErrors.totalCost = 'Total cost must be greater than 0'
        }
      }
      
      if (!formData.purchaseDate) newErrors.purchaseDate = 'Purchase date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const getUnitCategory = (unitType: string): string => {
    if (!availableUnits) return ''
    const unitDef = availableUnits.definitions[unitType]
    return unitDef ? unitDef.category : ''
  }

  const getRecommendedUnits = (category: string): string[] => {
    if (!availableUnits) return []
    
    const categoryUnits = availableUnits.grouped[category] || []
    return categoryUnits.slice(0, 5).map(unit => unit.type) // Top 5 units for the category
  }

  return {
    formData,
    setFormData,
    availableUnits,
    conversionLoading,
    errors,
    setErrors,
    handleInputChange,
    validateForm,
    getUnitCategory,
    getRecommendedUnits,
    convertPurchaseQuantity
  }
}